// Card #3117 -- fit the single-entry family's long-name type to the board.
//
// SingleEntryLayout's root is centred in the content area (the canvas minus
// the footer banner: 939.6px at 16:9 and 4:3, 819.6px at 2:1). With the longest
// real rider / horse / class names the root grew taller than that area and
// spilled out of both ends (16:9 worst case 973.7px tall against 939.6px, so
// the horse name at the bottom of the left column ran under the footer).
//
// Decision (card #3117): keep every name in full and shrink the type, but ONLY
// when the content would not fit. Rejected: clamping / ellipsising names (a
// live board must show the whole name), and rebalancing the columns (the
// overflow is vertical, wrapped name lines stacking up, so re-splitting width
// does not remove it and would change every typical board). A board whose names
// fit never gets the custom property, so it is byte-identical to before.
//
// The step is MEASURED rather than derived from name length: the number of
// wrapped lines depends on the font, the board shape and the other fields on
// screen, which a character count cannot know. `--single-entry-fit` is a
// multiplier the stylesheet applies to the long-text tiers
// (SingleEntryLayout.css).

export const FIT_PROPERTY = "--single-entry-fit";
export const FIT_AREA_SELECTOR = ".scoreboard-frame__content";

// 1 is "no fit" and is never written. The finest step that fits wins, so the
// names stay as large as the board allows. The floor keeps a pathological name
// readable; below it the board is allowed to overflow rather than shrink
// unreadably, and nothing is ever truncated.
export const FIT_STEPS = [0.95, 0.9, 0.85, 0.8, 0.75, 0.7, 0.65, 0.6, 0.55, 0.5, 0.45, 0.4];

// Pure: given a predicate "does the root fit at this multiplier", return the
// multiplier to apply (1 = leave the board alone). Kept separate from the DOM
// so the stepping can be unit tested without a layout engine.
export function chooseFitStep(fitsAt) {
  if (fitsAt(1)) return 1;
  for (const step of FIT_STEPS) {
    if (fitsAt(step)) return step;
  }
  return FIT_STEPS[FIT_STEPS.length - 1];
}

// Measure in layout units (offsetHeight / clientHeight), not
// getBoundingClientRect: a letterboxed board is scaled by a CSS transform,
// which scales rects but not layout, and the comparison must stay in one unit.
// Returns the multiplier left on the root (1 = property absent).
export function fitSingleEntryRoot(root) {
  if (!root) return 1;
  const area = root.closest(FIT_AREA_SELECTOR);
  if (!area || !area.clientHeight) return 1;
  // The probe leaves the root holding the multiplier it last tried. chooseFitStep
  // stops at the first that fits (or at the floor), so that is the chosen one, and
  // for 1 the probe has already removed the property (so a stale fit from a
  // previous, longer-named ride is cleared before every measurement).
  const step = chooseFitStep((multiplier) => {
    if (multiplier === 1) root.style.removeProperty(FIT_PROPERTY);
    else root.style.setProperty(FIT_PROPERTY, String(multiplier));
    return root.offsetHeight <= area.clientHeight;
  });
  return step;
}
